import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const fiveBottlenecks = [
  {
    label: '周期长',
    desc: '一节45分钟课程需完整观看、记录、判断、打分、写评语。一两节课没有问题，几千节课就会遇到时间瓶颈。',
    icon: 'ri-timer-line',
    color: 'primary',
  },
  {
    label: '资源消耗大',
    desc: '组织专家、安排视频、分配评审、汇总结果、处理分歧，背后需要大量人力和组织成本，长期只靠人工压力越来越大。',
    icon: 'ri-team-line',
    color: 'accent',
  },
  {
    label: '尺度容易出现差异',
    desc: '专家关注点可能不同：有的更重视理论阐释，有的更重视课堂互动，有的更重视价值升华。没有统一评价表和证据链，结果很难解释清楚。',
    icon: 'ri-ruler-line',
    color: 'primary',
  },
  {
    label: '证据难回溯',
    desc: '专家写"问题意识较强"这个判断可能准确，但管理者和教师会追问：哪个问题？哪个时间点？学生怎么回应？',
    icon: 'ri-search-eye-line',
    color: 'accent',
  },
  {
    label: '优秀经验难迁移',
    desc: '很多优秀教师的课堂经验非常宝贵，但往往停留在一次比赛、一场听课、一份评语里，青年教师很难看到具体片段和过程。',
    icon: 'ri-share-forward-line',
    color: 'primary',
  },
];

export default function P12TraditionalLimits({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-20 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-16 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div ref={titleRef} className="text-center mb-8 md:mb-10">
          <span className={`inline-block text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            传统听评课的局限
          </span>
          <h2 className={`font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            难以支撑<span className="text-primary-600">大规模、常态化、可追溯</span>评价
          </h2>
          <p className={`text-base text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            不是否定专家，而是当评价对象从几节课扩展到几千节、几万节课时，传统方式会遇到现实瓶颈
          </p>
        </div>

        {/* Central flow */}
        <div className={`flex items-center justify-center gap-3 md:gap-4 mb-8 transition-all duration-700 delay-300 flex-wrap ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          {['专家听课', '写评语', '分数汇总', '结果反馈'].map((step, i) => (
            <div key={step} className="flex items-center gap-3 md:gap-4">
              <div className="px-4 py-2.5 rounded-xl glass border-white/60 text-sm font-body font-medium text-foreground-700 whitespace-nowrap">
                {step}
              </div>
              {i < 3 && <i className="ri-arrow-right-line text-foreground-300 text-sm" />}
            </div>
          ))}
        </div>

        {/* Five bottlenecks */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {fiveBottlenecks.map((bn, i) => (
            <div
              key={bn.label}
              className={`group relative p-5 md:p-6 rounded-2xl transition-all duration-500 hover:scale-[1.02] ${bn.color === 'accent' ? 'glass-gold border-accent-200/30' : 'glass-liquid border-primary-200/30'}`}
              style={{ transitionDelay: `${500 + i * 100}ms` }}
            >
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bn.color === 'accent' ? 'bg-accent-50/80 border border-accent-200/40' : 'glass-blue border-primary-200/40'}`}>
                    <i className={`${bn.icon} text-lg ${bn.color === 'accent' ? 'text-accent-600' : 'text-primary-600'}`} />
                  </div>
                  <div className="w-6 h-6 rounded-full bg-red-50/80 border border-red-200/40 flex items-center justify-center">
                    <i className="ri-close-line text-sm text-red-400" />
                  </div>
                </div>
                <h4 className="font-heading text-base font-bold text-foreground-800 mb-2">{bn.label}</h4>
                <p className="text-xs md:text-sm text-foreground-500 font-body leading-relaxed">{bn.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className={`mt-6 text-center transition-all duration-1000 delay-900 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="inline-flex items-center gap-2 px-5 py-3 rounded-xl glass border-white/50">
            <i className="ri-lightbulb-flash-line text-accent-500" />
            <span className="text-sm font-body text-foreground-600">
              传统听评课<strong className="text-primary-700">太有价值</strong>，才需要用AI把这些经验<strong className="text-accent-700">保留下来、放大出来、验证出来</strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}